import type { ReactElement } from 'react';
import { OrderInquiryCard, useMyOrderInquiriesQuery } from '@/entities/order-inquiry';
import { Button } from '@/shared/ui';
import { OrderInquiryPolicyNotice } from './order-inquiry-policy-notice';
import { OrderInquiryWritableNotice } from './order-inquiry-writable-notice';

interface MyOrderInquiriesProps {
  readonly orderId: string;
}

/**
 * 내 주문 상세 아래 "교환·환불 문의" 섹션(문의 설계 §9) — 안내 문구(초안), 접수 가능 상태별 버튼/안내, 문의 내역(최신순).
 * 주문 상세 위젯(MyOrderDetail)은 배송비 작업이 고치는 중이라 건드리지 않고 페이지가 이 위젯을 그 아래에 둔다.
 */
export const MyOrderInquiries = ({ orderId }: MyOrderInquiriesProps): ReactElement | null => {
  const query = useMyOrderInquiriesQuery(orderId);
  if (query.isPending) return null;
  if (query.isError) {
    return (
      <section
        aria-label="교환·환불 문의"
        className="flex flex-col items-start gap-3 border-t border-foreground/10 pt-10 print:hidden"
      >
        <p className="text-sm text-destructive">문의 내역을 불러오지 못했어요.</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          다시 시도
        </Button>
      </section>
    );
  }
  const { inquiries, writable } = query.data;
  if (inquiries.length === 0 && writable === 'NOT_ELIGIBLE') return null;
  return (
    <section
      aria-label="교환·환불 문의"
      className="flex flex-col gap-6 border-t border-foreground/10 pt-10 print:hidden"
    >
      <p
        aria-hidden
        className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
      >
        Exchange · Refund
      </p>
      <h2 className="text-2xl leading-tight font-bold tracking-[-0.02em]">교환·환불 문의</h2>
      <OrderInquiryPolicyNotice />
      <OrderInquiryWritableNotice orderId={orderId} writable={writable} />
      {inquiries.length > 0 ? (
        <div className="flex flex-col divide-y divide-foreground/10 border-y border-foreground/10">
          {inquiries.map((inquiry) => (
            <OrderInquiryCard key={inquiry.id} inquiry={inquiry} />
          ))}
        </div>
      ) : null}
    </section>
  );
};
